// Top-level modal orchestrator: viewstate machine over the operation's
// lifecycle. preflight -> running -> result|error.
//
// Uses the new async API (POST /hard-restart-async + GET /operations/{id}).
// The legacy sync endpoint stays available; this modal always uses async.

import { Modal } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import { api } from '@/api/client'
import { ErrorView } from './ErrorView'
import { LiveView } from './LiveView'
import { PreflightView } from './PreflightView'
import { ResultView } from './ResultView'
import { useOperationPolling } from './useOperationPolling'
import type { StartHardRestartResponse } from './types'

export interface HardRestartTarget {
  tenantId: number
  instanceId: string
  containerName: string
  image?: string
  uptime?: string
  /** When provided, modal opens directly into LiveView for an in-flight op
   *  (e.g. user reopened the modal after closing it during a previous run). */
  resumeOperationId?: string
}

interface Props {
  target: HardRestartTarget | null
  onClose: () => void
}

type View = 'preflight' | 'live' | 'result' | 'error'

export function HardRestartModal({ target, onClose }: Props) {
  const [reason, setReason] = useState('')
  const [cleanVolumes, setCleanVolumes] = useState(false)
  const [operationId, setOperationId] = useState<string | null>(null)
  const qc = useQueryClient()

  // Reset on target change / close
  useEffect(() => {
    if (!target) {
      setReason('')
      setCleanVolumes(false)
      setOperationId(null)
      return
    }
    if (target.resumeOperationId) {
      setOperationId(target.resumeOperationId)
    }
  }, [target])

  const opQuery = useOperationPolling({
    tenantId: target?.tenantId ?? 0,
    instanceId: target?.instanceId ?? '',
    operationId,
  })
  const operation = opQuery.data ?? null

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error('no target')
      const url = `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/docker/${encodeURIComponent(
        target.containerName,
      )}/hard-restart-async`
      const res = await api.post<StartHardRestartResponse>(url, {
        reason: reason.trim(),
        confirmation: true,
        clean_volumes: cleanVolumes,
      })
      return res.data
    },
    onSuccess: data => {
      setOperationId(data.operation_id)
      notifications.show({
        color: 'orange',
        title: 'Hard restart iniciado',
        message: `op_id ${data.operation_id.slice(0, 8)}…`,
      })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Falha ao iniciar', message: err.message })
    },
  })

  // Decide current view from state
  let view: View = 'preflight'
  if (operation) {
    if (operation.status === 'running') view = 'live'
    else if (operation.status === 'success') view = 'result'
    else view = 'error' // failed | cancelled
  } else if (operationId) {
    view = 'live' // polling, before first response
  }

  // Refetch container list when operation reaches a terminal state
  useEffect(() => {
    if (operation && operation.status !== 'running') {
      qc.invalidateQueries({ queryKey: ['docker-containers'] })
      qc.invalidateQueries({ queryKey: ['inventory-ec2'] })
    }
  }, [operation, qc])

  const handleClose = useCallback(() => {
    // If the operation is still running, the modal closes but the orchestrator
    // keeps going server-side. The pill on EC2DockerTable signals the in-flight
    // op and re-opens the modal in LiveView.
    onClose()
  }, [onClose])

  const handleRetry = () => {
    setOperationId(null)
    // reuse the existing reason + clean_volumes — operator confirmed once
    startMutation.mutate()
  }

  if (!target) return null

  const closeable =
    view !== 'live' /* preflight + result + error are user-driven */ ||
    operation?.status !== 'running'

  return (
    <Modal
      opened={!!target}
      onClose={handleClose}
      withCloseButton
      title={
        <span style={{ fontFamily: '"Barlow", sans-serif', fontWeight: 700 }}>
          Hard Restart
        </span>
      }
      size={view === 'preflight' ? 'md' : 'xl'}
      closeOnClickOutside={closeable}
      closeOnEscape={closeable}
      overlayProps={{ blur: 2 }}
    >
      <AnimatePresence mode="wait">
        {view === 'preflight' && (
          <PreflightView
            key="preflight"
            containerName={target.containerName}
            image={target.image}
            uptime={target.uptime}
            reason={reason}
            onReasonChange={setReason}
            cleanVolumes={cleanVolumes}
            onCleanVolumesChange={setCleanVolumes}
            onCancel={handleClose}
            onStart={() => startMutation.mutate()}
            starting={startMutation.isPending}
          />
        )}
        {view === 'live' && operation && (
          <LiveView key="live" operation={operation} image={target.image} />
        )}
        {view === 'live' && !operation && (
          <LiveView
            key="live-pending"
            operation={{
              id: operationId ?? '',
              tenant_id: target.tenantId,
              instance_id: target.instanceId,
              container_name: target.containerName,
              operation_type: 'hard_restart',
              status: 'running',
              current_phase: null,
              phases: [],
              reason,
              clean_volumes: cleanVolumes,
              started_by_user_id: 0,
              started_at: new Date().toISOString(),
              ended_at: null,
              output_meta: {},
            }}
            image={target.image}
          />
        )}
        {view === 'result' && operation && (
          <ResultView key="result" operation={operation} onClose={handleClose} />
        )}
        {view === 'error' && operation && (
          <ErrorView
            key="error"
            operation={operation}
            onClose={handleClose}
            onRetry={handleRetry}
          />
        )}
      </AnimatePresence>
    </Modal>
  )
}
